/**
 * ImportModuleCheck — electronic-module check step in the bulk import flow.
 *
 * The digital-watch counterpart of ImportCalibreCheck. Where the calibre step
 * resolves mechanical/quartz calibre names against the custom-calibre DB, this
 * step resolves digital-watch *module* identifiers (e.g. Casio "3229") against
 * the shared `electronicModules` collection.
 *
 * Mirror map (calibre → module):
 *   ImportCalibreCheck            → ImportModuleCheck (this file)
 *   entry.movement                → entry.module            (source identifier)
 *   custom_calibres / getCustomCalibres → electronicModules / getElectronicModules
 *   lookupCalibre (CalibreCorner) → (no remote lookup — modules are local-only)
 *   saveCustomCalibre             → saveElectronicModule
 *   CalibreActionToolbar          → ElectronicModuleAiLookup (per-row AI/create)
 *   CalibreMapEntry               → ModuleMapEntry
 *   ExtractionRule targetField 'calibre'/'movement' → targetField 'module'
 *
 * Unlike calibres, modules are not brand-scoped (they're manufacturer-attributed)
 * and have no CalibreCorner-style remote lookup — matching is an instant, local,
 * case-insensitive lookup against `name` + `alsoKnownAs[]`. Anything left
 * unmatched is auto-created at save time by BulkImport's module link pre-pass
 * (the digital counterpart of the calibre link), so proceeding without creating
 * is safe — the button here is just an early, reviewable create.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import { ElectronicModuleAiLookup } from '../ElectronicModules/ElectronicModuleAiLookup';
import {
  getElectronicModules,
  saveElectronicModule,
  addAliasToElectronicModule,
} from '../../services/firebase';
import type { ElectronicModule } from '../../types';
import type { ExtractionRule } from './ImportCalibreCheck';
import toast from 'react-hot-toast';

interface ScrapedEntry {
  module?: string;
  [key: string]: unknown;
}

interface ModuleStatus {
  moduleName: string;
  normalizedName?: string;
  count: number;
  status: 'matched' | 'unmatched';
  data?: ElectronicModule;
}

const EXTRACTION_SOURCES = [
  { value: 'description', label: 'Description' },
  { value: 'title', label: 'Title' },
  { value: 'variant', label: 'Variant' },
  { value: 'tags', label: 'Tags' },
  { value: 'productName', label: 'Product Name' },
];

let ruleId = 0;

export interface ModuleMapEntry {
  module: ElectronicModule;
  normalizedName?: string;
}

interface ImportModuleCheckProps {
  entries: ScrapedEntry[];
  brandName: string;
  /**
   * Shared extraction-rules list (with the calibre step). Only rules whose
   * `targetField` is `'module'` are used here; the rest belong to the calibre
   * step. Editing is shared so a module rule can be authored in either step.
   */
  extractionRules: ExtractionRule[];
  onExtractionRulesChange: (rules: ExtractionRule[]) => void;
  onProceed: (moduleMap: Map<string, ModuleMapEntry>) => void;
  onBack: () => void;
}

export function ImportModuleCheck({ entries, brandName, extractionRules, onExtractionRulesChange, onProceed, onBack }: ImportModuleCheckProps) {
  const [modules, setModules] = useState<ModuleStatus[]>([]);
  const [showJson, setShowJson] = useState(false);
  const [jsonLimit, setJsonLimit] = useState(10);
  const [testResults, setTestResults] = useState<Map<string, number> | null>(null);
  const [localDb, setLocalDb] = useState<ElectronicModule[]>([]);
  const [localDbLoaded, setLocalDbLoaded] = useState(false);
  const [bulkCreating, setBulkCreating] = useState(false);

  // Load the full electronic-module collection once on mount so matches resolve
  // instantly (no per-row query). Mirrors ImportCalibreCheck's getCustomCalibres.
  useEffect(() => {
    let cancelled = false;
    getElectronicModules()
      .then((data) => {
        if (cancelled) return;
        setLocalDb(data);
        setLocalDbLoaded(true);
      })
      .catch((err) => {
        console.error('Failed to load electronic modules for local matching:', err);
        if (!cancelled) setLocalDbLoaded(true);
      });
    return () => { cancelled = true; };
  }, []);

  // Collapse whitespace + lowercase for case-insensitive, whitespace-forgiving keys.
  const normalizeKey = (s: string) => s.trim().replace(/\s+/g, ' ').toLowerCase();

  // Fast lookup index: lowercased name & alsoKnownAs → module. Modules are NOT
  // brand-scoped (they're manufacturer-attributed), so — unlike the calibre
  // index — there is no brand filter here.
  const localIndex = useMemo(() => {
    const idx = new Map<string, ElectronicModule>();
    for (const mod of localDb) {
      const keys = [mod.name, ...(mod.alsoKnownAs || [])];
      for (const k of keys) {
        if (k && k.trim()) idx.set(normalizeKey(k), mod);
      }
    }
    return idx;
  }, [localDb]);

  // Extract unique module identifiers from entries: the `module` field (the
  // #243-extracted digital calibre) plus any module-targeted extraction rules.
  const extractModules = useCallback(() => {
    const map = new Map<string, number>();

    entries.forEach((e) => {
      // From the module field
      const mod = (e.module || '').trim();
      if (mod && mod.length > 1) {
        map.set(mod, (map.get(mod) || 0) + 1);
      }

      // From module-targeted extraction rules only
      for (const rule of extractionRules) {
        if (rule.targetField !== 'module') continue;
        const fieldVal = String(e[rule.sourceField] || '');
        if (!fieldVal) continue;
        try {
          const regex = new RegExp(rule.pattern, 'i');
          const match = fieldVal.match(regex);
          if (match) {
            const extracted = rule.replaceWith?.trim() || (match[1] || match[0]).trim();
            if (extracted && extracted.length > 1) {
              map.set(extracted, (map.get(extracted) || 0) + 1);
            }
          }
        } catch {
          // invalid regex — skip
        }
      }
    });

    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ moduleName: name, count }));
  }, [entries, extractionRules]);

  const moduleExtractionRules = useMemo(
    () => extractionRules.filter((r) => r.targetField === 'module'),
    [extractionRules],
  );

  // Resolve matches once both the collection and the extracted list are ready.
  useEffect(() => {
    if (!localDbLoaded) return; // avoid flicker — wait for the module collection
    const extracted = extractModules();
    setModules((prev) => {
      // Preserve rows the user explicitly matched (AI-link or manual create):
      // their extracted string may not equal the linked module's canonical name
      // (e.g. "5476" → "Casio Module 5476"), so a naive rebuild from localIndex
      // would clobber them back to "will create" and drop the resolved id. Keyed
      // by extracted moduleName. (Mirrors ImportCalibreCheck's miss branch, which
      // returns the row unchanged rather than forcing it back to unmatched.)
      const priorByName = new Map(prev.map((m) => [m.moduleName, m]));
      return extracted.map((mod): ModuleStatus => {
        const hit = localIndex.get(normalizeKey(mod.moduleName));
        if (hit) {
          return { ...mod, status: 'matched', data: hit, normalizedName: hit.name };
        }
        const prior = priorByName.get(mod.moduleName);
        if (prior && prior.status === 'matched' && prior.data) {
          return { ...prior, count: mod.count };
        }
        return { ...mod, status: 'unmatched' };
      });
    });
  }, [extractModules, localIndex, localDbLoaded]);

  const unmatchedModules = useMemo(
    () => modules.filter((m) => m.status === 'unmatched'),
    [modules],
  );

  // Mark a row matched after a per-row link (via AI lookup). Deliberately does
  // NOT refresh the local collection: an AI-link either matches an existing
  // module (nothing created) or resolves the just-created doc into `data`, so a
  // setLocalDb refresh only re-fires the resolve effect. The resolve effect's
  // preserve-explicit-matches guard would keep the row anyway, but skipping the
  // refresh avoids the churn (and the demote race that motivated that guard).
  const markMatched = useCallback((moduleName: string, data?: ElectronicModule) => {
    setModules((prev) =>
      prev.map((m) =>
        m.moduleName === moduleName
          ? { ...m, status: 'matched', data, normalizedName: data?.name || moduleName.trim() }
          : m,
      ),
    );
  }, []);

  const handleAiLink = useCallback(
    async (moduleName: string, moduleId: string, linkedName: string) => {
      // Resolve the linked/created doc so the map carries full module data on
      // proceed. Fetch fresh but deliberately DON'T setLocalDb (see markMatched).
      let found: ElectronicModule | undefined;
      try {
        const fresh = await getElectronicModules();
        found = fresh.find((m) => m.id === moduleId);
      } catch {
        // ignore — fall back to a minimal module below
      }
      markMatched(moduleName, found || ({ id: moduleId, name: linkedName } as ElectronicModule));

      // Teach the linked module the extracted string as an alias so future
      // imports of that string match locally without needing AI (e.g. append
      // "5476" to "Casio Module 5476"). Skip when the string already equals the
      // module's name or an existing alias (any case) — arrayUnion is only
      // exact-match idempotent, so the case-insensitive guard is ours to do.
      const alias = moduleName.trim();
      const existingKeys = [linkedName, found?.name, ...(found?.alsoKnownAs || [])]
        .filter((k): k is string => !!k)
        .map((k) => k.trim().toLowerCase());
      if (alias && !existingKeys.includes(alias.toLowerCase())) {
        try {
          await addAliasToElectronicModule(moduleId, alias);
        } catch (err) {
          console.warn('Failed to add extracted string as module alias:', err);
        }
      }
    },
    [markMatched],
  );

  const handleCreateAllUnmatched = useCallback(async () => {
    if (unmatchedModules.length === 0) return;
    if (!window.confirm(
      `Create ${unmatchedModules.length} new electronic module${unmatchedModules.length === 1 ? '' : 's'}? Each is created as a bare entry (name only, no AI enrichment).`,
    )) return;
    setBulkCreating(true);
    let success = 0;
    let failed = 0;
    for (const mod of unmatchedModules) {
      try {
        const id = await saveElectronicModule({ name: mod.moduleName.trim() });
        setModules((prev) =>
          prev.map((m) =>
            m.moduleName === mod.moduleName
              ? { ...m, status: 'matched', data: { id, name: mod.moduleName.trim() } as ElectronicModule, normalizedName: mod.moduleName.trim() }
              : m,
          ),
        );
        success++;
      } catch (err) {
        console.error('Bulk create failed for module', mod.moduleName, err);
        failed++;
      }
    }
    getElectronicModules().then(setLocalDb).catch(() => {});
    setBulkCreating(false);
    if (failed === 0) {
      toast.success(`Created ${success} module${success === 1 ? '' : 's'}`);
    } else {
      toast.error(`Created ${success}, ${failed} failed`);
    }
  }, [unmatchedModules]);

  const handleProceed = () => {
    const moduleMap = new Map<string, ModuleMapEntry>();
    modules.forEach((mod) => {
      if (mod.status === 'matched' && mod.data) {
        moduleMap.set(mod.moduleName, { module: mod.data, normalizedName: mod.normalizedName });
      }
    });
    onProceed(moduleMap);
  };

  // Extraction rules management (module-targeted only; shared list)
  const addExtractionRule = () => {
    onExtractionRulesChange([...extractionRules, { id: `mr-${++ruleId}`, sourceField: 'description', pattern: '', targetField: 'module' }]);
  };

  const updateExtractionRule = (id: string, field: string, value: string) => {
    onExtractionRulesChange(extractionRules.map((r) => r.id === id ? { ...r, [field]: value } : r));
  };

  const removeExtractionRule = (id: string) => {
    onExtractionRulesChange(extractionRules.filter((r) => r.id !== id));
  };

  const matchedCount = modules.filter((m) => m.status === 'matched').length;
  const unmatchedCount = modules.length - matchedCount;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-sm text-gray-600">
            Found <span className="font-medium">{modules.length}</span> unique module{modules.length === 1 ? '' : 's'} across{' '}
            <span className="font-medium">{entries.length}</span> entries.
          </p>
          {localDbLoaded && modules.length > 0 && (
            <p className="text-xs text-gray-400 mt-1">
              {matchedCount} matched{unmatchedCount > 0 ? ` • ${unmatchedCount} will be created` : ''}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={onBack}>
            Back
          </Button>
        </div>
      </div>

      {/* Module extraction rules (shared list, module-targeted only) */}
      {moduleExtractionRules.length > 0 && (
        <div className="border rounded-lg p-3 space-y-2 bg-gray-50 overflow-x-auto">
          <p className="text-xs font-medium text-gray-500 uppercase">Module Extraction Rules</p>
          {moduleExtractionRules.map((rule) => (
            <div key={rule.id} className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500">Extract from</span>
              <select
                value={rule.sourceField}
                onChange={(e) => updateExtractionRule(rule.id, 'sourceField', e.target.value)}
                className="text-sm border rounded px-2 py-1"
              >
                {EXTRACTION_SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              <span className="text-xs text-gray-500">matching</span>
              <input
                type="text"
                value={rule.pattern}
                onChange={(e) => updateExtractionRule(rule.id, 'pattern', e.target.value)}
                placeholder="regex pattern (group 1 captured)..."
                className="text-sm border rounded px-2 py-1 flex-1 font-mono"
              />
              <span className="text-xs text-gray-500">→</span>
              <input
                type="text"
                value={rule.replaceWith || ''}
                onChange={(e) => updateExtractionRule(rule.id, 'replaceWith', e.target.value)}
                placeholder="use as-is (or type replacement)..."
                className="text-sm border rounded px-2 py-1 flex-1"
              />
              <span className="text-xs text-gray-500">into</span>
              <span className="text-xs font-medium text-gray-600 px-1">Module</span>
              {testResults?.has(rule.id) && (
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${testResults.get(rule.id)! > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                  {testResults.get(rule.id)}
                </span>
              )}
              <button onClick={() => removeExtractionRule(rule.id)} className="text-red-400 hover:text-red-600 text-xs">✕</button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Button variant="secondary" size="sm" onClick={addExtractionRule}>
          + Module Extraction Rule
        </Button>
        {moduleExtractionRules.length > 0 && (
          <Button variant="secondary" size="sm" onClick={() => {
            const counts = new Map<string, number>();
            for (const rule of moduleExtractionRules) {
              let matchCount = 0;
              for (const e of entries) {
                const fieldVal = String((e as Record<string, unknown>)[rule.sourceField] || '');
                if (!fieldVal) continue;
                try {
                  const regex = new RegExp(rule.pattern, 'i');
                  if (regex.test(fieldVal)) matchCount++;
                } catch { /* invalid regex */ }
              }
              counts.set(rule.id, matchCount);
            }
            setTestResults(counts);
          }}>
            Test Rules
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={() => { setShowJson(true); setJsonLimit(10); }}>
          View JSON
        </Button>
      </div>

      {/* Module table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="max-h-80 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-16">Used</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-28">Status</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Info</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {modules.map((mod, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono text-xs text-gray-900">
                    {mod.moduleName}
                    {mod.normalizedName && mod.normalizedName !== mod.moduleName && (
                      <span className="text-blue-600 ml-1">→ {mod.normalizedName}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-500">{mod.count}×</td>
                  <td className="px-3 py-2">
                    {mod.status === 'matched' && <Badge variant="success">Matched</Badge>}
                    {mod.status === 'unmatched' && <Badge variant="warning">Will create</Badge>}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-400">
                    {mod.data?.displayType && <span className="capitalize">{mod.data.displayType}</span>}
                    {mod.data?.battery && <span className="ml-2">{mod.data.battery}</span>}
                    {mod.data?.lightType && <span className="ml-2">{mod.data.lightType}</span>}
                  </td>
                  <td className="px-3 py-2">
                    {mod.status === 'unmatched' && (
                      <ElectronicModuleAiLookup
                        variant="mini"
                        disabled={bulkCreating}
                        buildInput={() => ({
                          brand: brandName,
                          legacyValues: { module: mod.moduleName },
                          hint: mod.moduleName,
                        })}
                        onLink={(moduleId, moduleName) => { void handleAiLink(mod.moduleName, moduleId, moduleName); }}
                      />
                    )}
                  </td>
                </tr>
              ))}
              {modules.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-gray-400">No modules found — digital watches only, or add a module extraction rule</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-xs bg-blue-50 text-blue-700 p-3 rounded">
        Matched modules link to the shared electronic-module database instantly. Anything left as "Will create" is
        created automatically at import (as a bare name-only entry) and linked to each digital ref — use "Create All"
        or the ✨ AI lookup to review and enrich them first.
      </p>

      {/* Action buttons */}
      <div className="flex justify-between items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {unmatchedModules.length > 0 && (
            <Button
              variant="secondary"
              onClick={handleCreateAllUnmatched}
              disabled={bulkCreating}
              loading={bulkCreating}
            >
              Create All Unmatched ({unmatchedModules.length})
            </Button>
          )}
        </div>
        {/* Gated on the module collection landing, for the same reason the
            calibre step's Proceed is: `handleProceed` builds its map from
            `modules`, which is empty until the resolve effect has run, so an
            early click imports every reference with no module link and nothing
            to show that it happened. */}
        <Button
          onClick={handleProceed}
          disabled={!localDbLoaded}
          title={
            !localDbLoaded
              ? 'Waiting for the module collection — proceeding now would import every reference with no module link.'
              : undefined
          }
        >
          {!localDbLoaded
            ? 'Resolving…'
            : modules.length === 0
              ? 'Skip & Proceed →'
              : `Proceed → (${matchedCount} matched)`}
        </Button>
      </div>

      {/* JSON Viewer */}
      {showJson && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowJson(false)}>
          <div className="bg-white rounded-lg shadow-xl w-[90vw] max-w-4xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="font-medium">Import Data JSON ({entries.length} entries)</h3>
              <button onClick={() => setShowJson(false)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <pre className="flex-1 overflow-auto p-4 text-xs font-mono bg-gray-900 text-green-400">
              {JSON.stringify(entries.slice(0, jsonLimit), null, 2)}
            </pre>
            <div className="px-4 py-2 border-t flex items-center justify-between">
              <span className="text-xs text-gray-400">
                Showing {Math.min(jsonLimit, entries.length)} of {entries.length} entries
              </span>
              {jsonLimit < entries.length && (
                <button
                  onClick={() => setJsonLimit((prev) => Math.min(prev + 20, entries.length))}
                  className="text-xs text-brand-600 hover:text-brand-800 font-medium"
                >
                  Load more ({Math.min(20, entries.length - jsonLimit)} more)
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
